import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Loader2, Pencil, Trash2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { can, useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DataTable, DemoBadge, Field, PageHeader, SearchBox, StateSelect } from "@/components/common";

export function CrudPage({ title, subtitle, endpoint, columns, fields, defaults = {},
  testid, searchPlaceholder, onRow, mapOut, mapIn, createLabel, listenForNew = false }) {
  const { user } = useAuth();
  const [data, setData] = useState({ items: [], total: 0, pages: 1 });
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(endpoint, { params: { q, page, limit: 25 } });
      setData(r.data);
    } catch (e) {
      toast.error(errMsg(e));
    }
    setLoading(false);
  }, [endpoint, q, page]);

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [load]);

  useEffect(() => {
    if (listenForNew && new URLSearchParams(window.location.search).get("new") === "1") {
      setEditing(null);
      setForm({ ...defaults });
      setOpen(true);
    }
  }, [listenForNew]);

  const openEdit = (row) => {
    setEditing(row);
    setForm(mapIn ? mapIn(row) : { ...defaults, ...row });
    setOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = mapOut ? mapOut(form) : form;
      const r = editing
        ? await api.put(`${endpoint}/${editing.id}`, payload)
        : await api.post(endpoint, payload);
      const warnings = r.data?.warnings || [];
      warnings.forEach((w) => toast.warning(w));
      toast.success(editing ? "Updated successfully" : "Created successfully");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
    setSaving(false);
  };

  const remove = async (row) => {
    if (!window.confirm("Archive this record? Financial history is preserved.")) return;
    try {
      await api.delete(`${endpoint}/${row.id}`);
      toast.success("Archived");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const writable = can(user?.role, endpoint.split("/").filter(Boolean)[0], true);
  const cols = [...columns];
  if (writable) {
    cols.push({
      key: "_actions", label: "", align: "right",
      render: (row) => (
        <div className="flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
          <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => openEdit(row)} data-testid={`${testid}-edit-${row.id}`}>
            <Pencil className="h-3.5 w-3.5" />
          </Button>
          <Button size="icon" variant="ghost" className="h-7 w-7 text-red-500" onClick={() => remove(row)} data-testid={`${testid}-delete-${row.id}`}>
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      ),
    });
  }

  return (
    <div>
      <PageHeader title={title} subtitle={subtitle} testid={`${testid}-header`}
        actions={
          <>
            <SearchBox value={q} onChange={(v) => { setQ(v); setPage(1); }}
              placeholder={searchPlaceholder} testid={`${testid}-search`} />
            {writable && (
              <Button className="bg-[#0D4F4A] hover:bg-[#136A63]" data-testid={`${testid}-create-button`}
                onClick={() => { setEditing(null); setForm({ ...defaults }); setOpen(true); }}>
                <Plus className="h-4 w-4 mr-1" /> {createLabel || "New"}
              </Button>
            )}
          </>
        } />
      <DataTable columns={cols} rows={data.items} loading={loading} testid={`${testid}-table`}
        page={data.page} pages={data.pages} total={data.total} onPage={setPage}
        onRow={onRow || (writable ? openEdit : undefined)} />
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid={`${testid}-form-dialog`}>
          <DialogHeader>
            <DialogTitle className="font-display text-xl">
              {editing ? "Edit" : "New"} {title.replace(/s$/, "")}
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-2">
            {fields.map((f) => (
              <div key={f.key} className={f.full ? "sm:col-span-2" : ""}>
                <Field label={f.label} required={f.required} hint={f.hint}>
                  {f.type === "textarea" ? (
                    <Textarea value={form[f.key] || ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                      data-testid={`${testid}-field-${f.key}`} className="bg-white border-[#E5E0D8]" />
                  ) : f.type === "select" ? (
                    <Select value={String(form[f.key] ?? "")} onValueChange={(v) => setForm({ ...form, [f.key]: f.numeric ? Number(v) : v })}>
                      <SelectTrigger data-testid={`${testid}-field-${f.key}`} className="bg-white border-[#E5E0D8]">
                        <SelectValue placeholder={f.placeholder || "Select…"} />
                      </SelectTrigger>
                      <SelectContent>
                        {(f.options || []).map((o) => (
                          <SelectItem key={String(o.value)} value={String(o.value)}>{o.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : f.type === "state" ? (
                    <StateSelect value={form[f.key] || ""} onChange={(v) => setForm({ ...form, [f.key]: v })}
                      testid={`${testid}-field-${f.key}`} />
                  ) : f.type === "checkbox" ? (
                    <div className="flex items-center gap-2 pt-2">
                      <Checkbox checked={!!form[f.key]} onCheckedChange={(v) => setForm({ ...form, [f.key]: !!v })}
                        data-testid={`${testid}-field-${f.key}`} />
                      <span className="text-sm">{f.checkboxLabel || f.label}</span>
                    </div>
                  ) : (
                    <Input type={f.type || "text"} value={form[f.key] ?? ""}
                      onChange={(e) => setForm({ ...form, [f.key]: f.type === "number" ? (e.target.value === "" ? "" : Number(e.target.value)) : e.target.value })}
                      data-testid={`${testid}-field-${f.key}`} className="bg-white border-[#E5E0D8]"
                      placeholder={f.placeholder} />
                  )}
                </Field>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid={`${testid}-form-cancel`}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-[#0D4F4A] hover:bg-[#136A63]" data-testid={`${testid}-form-submit`}>
              {saving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />} Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export { DemoBadge };
